import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { showCommonPopupAction } from 'js/components/common/popups/CommonPopup';
import { showConfirmationPopupAction } from 'js/components/common/popups/ConfirmationPopup';
import { showLoginPopupAction } from 'js/components/popups/LoginPopup';
import { showJumioVerifyStatusPopupAction } from 'js/components/popups/JumioVerifyPopup';
import { showLogoutNotificationPopupAction } from 'js/components/popups/LogoutNotificationPopup';
import { useNavigateToPopupBackgroundPage } from 'js/components/system/Router/components/PageRouter/hooks';
import { showTwoFactorAuthenticationPopupAction } from 'js/components/popups/TwoFactorAuthenticationPopup';
import { useSmartClosePopups, disablePopupsAction, enablePopupsAction } from 'js/components/common/PopupManager';
import { showBonusBuyInPopupAction } from 'js/components/popups/BonusBuyInPopup';
import { showTransparentPopupAction } from 'js/components/common/popups/TransparentPopup';
import { showCardPopupAction } from 'js/components/common/popups/CardPopup';
import { showGamblingSessionLimitsPopupAction } from 'js/components/popups/GamblingSessionLimitsPopup';
import { showMultiFactorAuthenticationPopupAction } from 'js/components/popups/MultiFactorAuthenticationPopup';
import { showReCaptchaPopupAction } from 'js/components/popups/ReCaptchaPopup';
import { showQuestionnairePopupAction } from 'js/components/popups/QuestionnairePopup';
import { showSimpleDialogInfoPopupAction } from 'js/components/popups/SimpleDialogInfoPopup';
import { useGetButtons } from 'js/components/popups/AdvancedPlayerMessage/hooks';

const actionsMap = {
    common: showCommonPopupAction,
    confirmation: showConfirmationPopupAction,
    login: showLoginPopupAction,
    jumioVerificationStatus: showJumioVerifyStatusPopupAction,
    logoutNotification: showLogoutNotificationPopupAction,
    twoFactorAuthentication: showTwoFactorAuthenticationPopupAction,
    multiFactorAuthentication: showMultiFactorAuthenticationPopupAction,
    bonusBuyIn: showBonusBuyInPopupAction,
    transparent: showTransparentPopupAction,
    card: showCardPopupAction,
    gamblingSessionLimits: showGamblingSessionLimitsPopupAction,
    reCaptcha: showReCaptchaPopupAction,
    questionnaire: showQuestionnairePopupAction,
    simpleDialogInfo: showSimpleDialogInfoPopupAction
};

export const usePopupPublicAPI = () => {
    const dispatch = useDispatch();
    const closePopups = useSmartClosePopups();
    const getButtons = useGetButtons();
    const navigateToPopupBackgroundPage = useNavigateToPopupBackgroundPage();

    return useMemo(() => ({
        /**
         * Show a popup. A few popup types are supported:<br><br>
         * 'common' popup by default shows only one button with default text which just closes the popup.
         * supported params: id, content, onClose, btnTitle, closeAll, layer, isModal, articleId, onOverlayClick, layerClassNames, buttons, children, controls, title, onTouchStart, className, classNames, style, iconType<br><br>
         * 'transparent' popup is just like a common popup, but without the popup's background structure.
         * supported params: id, content, onClose, btnTitle, closeAll, layer, isModal, articleId, onOverlayClick, layerClassNames, buttons, children, controls, title, onTouchStart, className, classNames, style, iconType<br><br>
         * 'confirmation' popup by default shows only two buttons with default text, however it is possible to override texts and actions when clicked.
         * supported params: title, onClose, onAccept, controls, onDecline, articleId, content, acceptBtnText, declineBtnText, onOverlayClick, layerClassNames, buttons, children, controls, onTouchStart, className, classNames, style, iconType<br><br>
         * 'login' popup just shows the login popup.
         * supported params: onClose, onLogin, title<br><br>
         * 'jumioVerificationStatus' popup with predefined title and content depends on status property passed to options.
         * supported params:status<br><br>
         * 'logoutNotification' after logout message popup
         * supported params: isLoginAgainPopup, onAccept, onDecline, className<br><br>
         * 'twoFactorAuthentication' popup with TwoFactorAuthentication component
         *  supported params: id, title, templateId, controls, onSuccess, resendHandler, onClose<br><br>
         * 'bonusBuyIn' bonus popup
         *  supported params: title, bonusCode, buyInWebContentId, termAndConditionWebContentId, layerClassNames, buttons, children, controls, title, onTouchStart, className, classNames, style, iconType, onClose, onOverlayClick
         * 'gamblingSessionLimits' popup with GamblingSessionLimits component
         * supported params: onSuccessLimitsSet
         * 'questionnaire' popup with list of questions required before increase player's limits
         *  supported params: id, title, onClose, onSuccess, formName, tkPrefix, daydepositlimit, weekdepositlimit, monthdepositlimit
         * 'simpleDialogInfo' popup to interact with "actionShowSimpleDialogInfo" data structures
         *  supported params: message, actionId, layer, acceptBtnText, declineBtnText, afterSubmitAction
         * @memberOf Playtech.API.popup
         * @function
         * @param {string}  [popupType=common]Values: 'common', 'confirmation', 'transparent', 'login', 'jumioVerificationStatus', 'logoutNotification', 'twoFactorAuthentication', 'bonusBuyIn', 'gamblingSessionLimits', 'questionnaire', 'simpleDialogInfo'.
         * @param {Object} params Parameters for the popup available for all popupTypes.
         * @param {string} [params.id] id of the popup.
         * @param {string} [params.title] Title of the popup.
         * @param {string} [params.content] Contents of the popup.
         * @param {string} [params.articleId] ID of the Web Content from the CMS to show instead of content.
         * @param {boolean} [params.isModal=true] Whether to show 'close' button.
         * @param {Function} [params.onClose] Callback function to process when player clicks 'close' button.
         * @param {Object[]} [params.buttons] Buttons of the popup. Empty array "[]" specified for popup without buttons. If buttons param is not specified - popup will show the default "OK" button.
         * @param {string} [params.buttons[].id]
         * @param {Function} [params.buttons[].onClick]
         * @param {string} [params.buttons[].className]
         * @param {string} params.buttons[].text
         * @param {string} [params.buttons[].view] Values: 'primary', 'secondary', 'readonly'.
         * @param {string} [params.buttons[].color] Values: 'success', 'negative', 'info', 'revert', 'generic'.
         * @param {string} [params.buttons[].size] Values: 'large', 'medium', 'small'.
         * @param {string} [params.acceptBtnText] Translation key or text to show on 'accept' button.
         * @param {string} [params.declineBtnText]  Translation key or text to show on 'decline' button.
         * @param {Function} [params.onAccept] Callback function to process when player clicks 'accept' button.
         * @param {Function} [params.onDecline] Callback function to process when player clicks 'decline' button.
         * @param {string} [params.status] Jumio status.
         * @param {string} [params.isLoginAgainPopup] Popup type to 'confirmation'.
         * @param {boolean} [params.className] {string} Providing styles to popup.
         * @param {string} [params.id] Will be passed to TwoFactorAuthentication component as property.
         * @param {string}  [params.title]Will be passed to TwoFactorAuthentication component as property.
         * @param {string} [params.templateId] Will be passed to TwoFactorAuthentication component as property. Values: 'two_factor_email', 'two_factor_sms'.
         * @param {Object} [params.controls] Will be passed to popup wrapper for controls.
         * @param {Function} [params.controls.onPrev]
         * @param {Function} [params.controls.onNext]
         * @param {string} [params.controls.label]
         * @param {Function} [params.onSuccess] Callback after success iapiValidateTwoFactorToken request.
         * @param {Function} [params.resendHandler] Function called on resend code flow.
         * @param {string} [params.btnTitle] Button title.
         * @param {boolean} [params.closeAll] Whether other popups should be closed.
         * @param {string} [params.layer] Layer witch should be used, this parameter should be used carefully.
         * @param {Function} [params.onLogin] Callback after success login process.
         * @example
         * Playtech.API.popup.showPopup({
         *     title: 'Title',
         *     content: 'Very important message!'
         * });
         * @example
         * Playtech.API.popup.showPopup('confirmation', {
         *     content: 'Would you accept this confirmation?',
         *     onAccept: () => {
         *         // do something if player accepts
         *     }
         * });
         */
        showPopup: (popupType, params) => {
            let action;
            let popupParams;

            if (typeof popupType === 'string') {
                action = actionsMap[popupType] || showCommonPopupAction;
                popupParams = params;
            } else {
                action = showCommonPopupAction;
                popupParams = popupType;
            }

            dispatch(action(popupParams));
        },

        /**
         * Closes one, several or all popups depending on passed params.
         * @memberOf Playtech.API.popup
         * @function
         * @param {Object|string} [shortcutOrParams]
         * @param {string[]} [shortcutOrParams.popups]
         * @param {string[]} [shortcutOrParams.exclude]
         * @param {string[]} [shortcutOrParams.excludeLayers]
         * @example
         * // will close single popup with ID 'mySpecificPopupID'
         * smartClosePopups('mySpecificPopupID');
         * @example
         * // will close all popups except for PLAYER_FREEZE_ACCOUNT_POPUP, LOGOUT_NOTIFICATION_POPUP, USER_VERIFY_POPUP
         * smartClosePopups('logout');
         * @example
         * // will close all popups except for WELCOME_POPUP popup and any popups
         * // that are part of ADVANCED_PLAYER_MESSAGE and ADVANCED_PLAYER_MESSAGE_MODAL layers
         * smartClosePopups('gameLaunch');
         * @example
         * // will close popups according to provided criteria
         * smartClosePopups({
         *     popups: ['popupToClose1', 'popupToClose2'],
         *     exclude: ['popupToStay1', 'popupToStay2'],
         *     excludeLayers: ['layerToStay1', 'layerToStay2']
         * });
         * @example
         * // will close all popups
         * smartClosePopups();
         */
        closePopups,

        /**
         * Disable all popups. Any popups that will be issued will not be visible.
         * But as soon as popups will be enabled -- all issued previous popups will become visible.
         * @memberOf Playtech.API.popup
         * @function
         * @example
         * Playtech.API.popup.disablePopups();
         */
        disablePopups: () => dispatch(disablePopupsAction()),

        /**
         * Enable all popups. All popups issued previously will become visible.
         * If this is not desired, please invoke {#closePopups} before.
         * @memberOf Playtech.API.popup
         * @function
         * @example
         * Playtech.API.popup.enablePopups();
         */
        enablePopups: () => dispatch(enablePopupsAction()),

        /**
         * Close page which is displayed as popup.
         * @memberOf Playtech.API.popup
         * @function
         * @example
         * Playtech.API.popup.closePageAsPopup();
         */
        closePageAsPopup: () => navigateToPopupBackgroundPage(),

        /**
         * Convert button configuration received from "advanced-dialog" event into a proper actinoable button configuration with onClose handler.
         * @memberOf Playtech.API.popup
         * @function
         * @param {Object} params
         * @param {string} [params.actionId] Action ID.
         * @param {string} [params.windowSessionId] Window Session ID.
         * @param {Object[]} [params.buttons] Buttons of the popup. Empty array "[]" specified for popup without buttons. If buttons param is not specified - popup will show the default "OK" button.
         * @param {string} [params.buttons[].id]
         * @param {Function} [params.buttons[].onClick]
         * @param {string} [params.buttons[].className]
         * @param {string} params.buttons[].text
         * @param {string} [params.buttons[].view] Values: 'primary', 'secondary', 'readonly'.
         * @param {string} [params.buttons[].color] Values: 'success', 'negative', 'info', 'revert', 'generic'.
         * @param {string} [params.buttons[].size] Values: 'large', 'medium', 'small'.
         * @example
         * Playtech.API.popup.getButtons({
         *     buttons: [],
         *     actionId: '...',
         *     windowSessionId: '...'
         * });
         */
        getButtons
    }), [dispatch, closePopups, getButtons, navigateToPopupBackgroundPage]);
};
